import { compose, filter, map, pick, deburr } from 'lodash/fp';
import { createSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { string } from 'fp-ts';
import { compactArray, getProp } from '@mews/fp';
import { composeProps, createPropsExtender, ifProps } from '@mews/framework';
import {
    ApplicationState,
    WithEnterpriseId,
    WithResourceCategoryId,
    WithResourceId,
    WithServiceId,
} from '@mews-commander/types';
import { OccupancyState } from '@mews-commander/data-access';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { getResourceCategoriesByServiceIds, getResourceCategoryShortName } from '../ducks/resourceCategories';
import { getResourceName, getResourceState, getResources } from '../ducks/resources';
import {
    getResourceCategoryAssignments,
    getResourceCategoryIdByResourceId,
} from '../ducks/resourceCategoryAssignments';
import { getServiceEnterpriseId } from '../ducks/services';
import { getResourceFeaturesByResourceId } from './resourceFeatures';

// @ts-expect-error $TSFixMeError: No overload matches this call.
const getAssignedCategoryIds = compose(map('categoryId'), filter);

const getNormalizedResources = (assignments: $TSFixMe, categories: $TSFixMe, resources: $TSFixMe) =>
    map(({ id, parentId }) => {
        // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 2.
        const categoryIds: string[] = getAssignedCategoryIds({ resourceId: id }, assignments);
        const assignedCategories = pick(categoryIds, categories);

        return {
            id,
            parentId,
            categories: assignedCategories,
        };
    }, resources);

export const getCategorizedResources = createSelector(
    getResourceCategoryAssignments,
    getResourceCategoriesByServiceIds,
    getResources,
    getNormalizedResources
);

const getResourceNameByServiceId = composeProps(
    (state: ApplicationState, { resourceId, enterpriseId }: WithEnterpriseId & WithResourceId) =>
        getResourceName(state, { id: resourceId, enterpriseId }),
    createPropsExtender(
        'enterpriseId',
        (state: ApplicationState, { serviceId }: WithServiceId & { resourceId: string }) =>
            getServiceEnterpriseId(state, { id: serviceId })
    )
);

const getResourceCategoryShortNameByResourceId = composeProps(
    ifProps(
        ['resourceCategoryId'],
        (state: ApplicationState, { resourceCategoryId, serviceId }: WithResourceCategoryId & WithServiceId) =>
            getResourceCategoryShortName(state, { id: resourceCategoryId, serviceId })
    ),
    createPropsExtender('resourceCategoryId', getResourceCategoryIdByResourceId)
);

export const getResourceNameWithCategoryDetail = createSelector(
    getResourceNameByServiceId,
    getResourceCategoryShortNameByResourceId,
    (resourceName, resourceCategoryShortName) => compactArray([resourceCategoryShortName, resourceName]).join(' ')
);

const getResourceStateByServiceId = composeProps(
    (state: ApplicationState, { resourceId, enterpriseId }: WithEnterpriseId & WithResourceId) =>
        getResourceState(state, { id: resourceId, enterpriseId }),
    createPropsExtender('enterpriseId', (state: ApplicationState, { serviceId }: WithServiceId) =>
        getServiceEnterpriseId(state, { id: serviceId })
    )
);

const getNormalizedSearchValue = (stringArray: string[]) =>
    pipe(stringArray.join(' '), deburr, string.toLowerCase, string.trim);

export const getResourceSearchValue = createSelector(
    getResourceNameWithCategoryDetail,
    getResourceFeaturesByResourceId,
    getResourceStateByServiceId,
    getProp<OccupancyState>()('occupancyState'),
    getContainer,
    (resourceDetail, features, resourceState, occupancyState, container) => {
        const { appLocalizationComponent } = container();
        const localizedResourceState = resourceState
            ? appLocalizationComponent.localizeEnum('ResourceState', resourceState)
            : '';
        const localizedOccupancyState = appLocalizationComponent.localizeEnum('OccupancyState', occupancyState);
        const featureNames = features.map(({ name }) => name);

        return getNormalizedSearchValue([
            resourceDetail,
            localizedResourceState,
            localizedOccupancyState,
            ...featureNames,
        ]);
    }
);
